import { CustomerForm } from "@/components/CustomerForm";
import { CustomerFormData } from "@/components/forms/types";
import { CartSummary } from "@/components/CartSummary";
import { Event } from "@/lib/types";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useState } from "react";

interface PaymentSectionProps {
  event: Event;
  seoUrl: string;
  selectedSeats: string[];
  vipTicketCount?: number;
  standingTickets: number;
  earlyBirdTickets: number;
  discountPercentage: number;
  setDiscountPercentage: (percentage: number) => void;
}

export const PaymentSection = ({
  event,
  seoUrl,
  selectedSeats,
  vipTicketCount = 0,
  standingTickets,
  earlyBirdTickets,
  discountPercentage,
  setDiscountPercentage,
}: PaymentSectionProps) => {
  const [currentTotal, setCurrentTotal] = useState(0);

  const handleCustomerSubmit = async (data: CustomerFormData) => {
    // Log detailed payment data for debugging
    console.log('Payment Details:', {
      eventId: event.id,
      eventTitle: event.title,
      template_type: event.templateType,
      vipSeats: selectedSeats,
      vipTicketCount,
      standingTickets,
      earlyBirdTickets,
      currentTotal,
      stripeAmountCents: Math.round(currentTotal * 100),
      currency: event.currency || 'AUD',
      tax_behavior: event.taxBehavior || 'inclusive',
      discountPercentage,
      priceBreakdown: {
        vipPrice: event.priceVip,
        standingPrice: event.priceGeneralAdmission,
        earlyBirdPrice: event.earlyBirdPrice,
        slayFeePercentage: event.slayFeePercentage,
        slayFeeFixed: event.slayFeeFixed,
        gstRate: event.inclusiveGstRate
      }
    });

    // Validate that at least one ticket is selected
    const vipQuantity = event.templateType === 'no_seating' ? selectedSeats.length : vipTicketCount;
    if (!event.id || (vipQuantity === 0 && standingTickets === 0 && earlyBirdTickets === 0)) {
      toast.error("Please select at least one ticket");
      return;
    }

    // Validate VIP ticket availability
    if (vipQuantity > 0 && vipQuantity > (event.quantityVip || 0)) {
      toast.error("Selected VIP tickets are no longer available");
      return;
    }

    // Validate Standing ticket availability
    if (standingTickets > 0 && standingTickets > (event.quantityStanding || 0)) {
      toast.error("Selected standing tickets are no longer available");
      return;
    }

    // Validate Early Bird ticket availability
    if (earlyBirdTickets > 0 && earlyBirdTickets > (event.quantityEarlyBird || 0)) {
      toast.error("Selected early bird tickets are no longer available");
      return;
    }

    // Convert to cents for Stripe
    const stripeAmount = Math.round(currentTotal * 100);

    // Build ticket description
    const ticketParts = [];
    if (vipQuantity > 0) {
      ticketParts.push(`${vipQuantity} VIP ticket${vipQuantity > 1 ? 's' : ''}`);
    }
    if (standingTickets > 0) {
      ticketParts.push(`${standingTickets} GA ticket${standingTickets > 1 ? 's' : ''}`);
    }
    if (earlyBirdTickets > 0) {
      ticketParts.push(`${earlyBirdTickets} Early Bird ticket${earlyBirdTickets > 1 ? 's' : ''}`);
    }
    const ticketDescription = ticketParts.join(", ");

    console.log('Initiating checkout with:', {
      vipQuantity,
      standingTickets,
      earlyBirdTickets,
      discountPercentage,
      finalTotal: currentTotal,
      stripeAmountCents: Math.round(currentTotal * 100),
      ticketDescription,
      currency: event.currency,
      tax_behavior: event.taxBehavior,
      checkoutData: {
        vipSeats: selectedSeats,
        vipTicketCount: event.templateType === 'vip_seating' ? vipTicketCount : undefined,
        standingTickets,
        earlyBirdTickets,
        eventId: event.id,
        customerEmail: data.email,
        totalAmount: Math.round(currentTotal * 100),
        discountPercentage,
        currency: event.currency || 'AUD',
        tax_behavior: event.taxBehavior || 'inclusive'
      }
    });

    try {
      const { data: checkoutData, error } = await supabase.functions.invoke('create-checkout', {
        body: {
          vipSeats: selectedSeats,
          vipTicketCount: event.templateType === 'vip_seating' ? vipTicketCount : undefined,
          standingTickets,
          earlyBirdTickets,
          eventId: event.id,
          customerInfo: data,
          totalAmount: Math.round(currentTotal * 100),
          discountPercentage,
          currency: event.currency || 'AUD',
          tax_behavior: event.taxBehavior || 'inclusive',
          customer_email: data.email,
          ticketDescription
        },
      });

      if (error) {
        console.error('Checkout error:', error);
        throw error;
      }

      if (checkoutData?.url) {
        console.log('Redirecting to Stripe checkout:', {
          sessionUrl: checkoutData.url,
          finalAmount: Math.round(currentTotal * 100)
        });
        window.location.href = checkoutData.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error("Failed to initiate checkout. Please try again.");
    }
  };

  return (
    <div className="grid md:grid-cols-2 gap-8">
      <CartSummary
        vipSeats={selectedSeats}
        vipTicketCount={vipTicketCount}
        standingTickets={standingTickets}
        earlyBirdTickets={earlyBirdTickets}
        vipPrice={parseFloat(event.priceVip)}
        standingPrice={parseFloat(event.priceGeneralAdmission)}
        earlyBirdPrice={parseFloat(event.earlyBirdPrice) || 0}
        discountPercentage={discountPercentage}
        event={event}
        onTotalChange={setCurrentTotal}
      />

      <div className="bg-gray-800/50 rounded-lg p-6">
        <h3 className="text-xl font-bold text-white mb-6">Your Information</h3>
        <CustomerForm
          onSubmit={handleCustomerSubmit}
          eventId={event.id}
          subtotal={selectedSeats.length * parseFloat(event.priceVip) +
            standingTickets * parseFloat(event.priceGeneralAdmission) +
            earlyBirdTickets * (parseFloat(event.earlyBirdPrice) || 0)}
          onPromoCodeApplied={setDiscountPercentage}
        />
      </div>
    </div>
  );
};
